/**
 * OZDAFNE Hypo Treatment Protocol
 *
 * Guidelines for treating hypoglycemia (low blood glucose).
 * Based on OZDAFNE poster "Treating a Hypo (Low Blood Glucose)".
 *
 * Last reviewed: February 2026
 */

// =============================================================================
// HYPO TREATMENT THRESHOLDS
// =============================================================================

export const HYPO_TREATMENT = {
  /** Blood glucose level requiring treatment (mmol/L) */
  THRESHOLD: 4.0,

  /** Amount of rapid-acting carbs to treat (in CPs) */
  TREATMENT_CPS: 1.5,

  /** Grams of carbohydrate for treatment (~15g) */
  TREATMENT_GRAMS: 15,

  /** Minutes to wait before rechecking */
  WAIT_MINUTES: 15,

  /** Repeat treatment if still below threshold */
  REPEAT_IF_BELOW: 4.0,
} as const;

// =============================================================================
// RAPID-ACTING CARB OPTIONS FOR HYPO TREATMENT
// Each option provides approximately 1.5 CPs (~15g) of rapid-acting carbs
// =============================================================================

export interface HypoTreatmentOption {
  name: string;
  amount: string;
  grams: number;
  icon?: string;
}

export const HYPO_TREATMENT_OPTIONS: HypoTreatmentOption[] = [
  {
    name: "Lucozade Energy",
    amount: "80 mL",
    grams: 15,
    icon: "cup-straw",
  },
  {
    name: "Jelly beans",
    amount: "6-10 beans",
    grams: 15,
    icon: "heart-fill",
  },
  {
    name: "Soft drink (regular)",
    amount: "150 mL",
    grams: 15,
    icon: "cup",
  },
  {
    name: "Fruit juice",
    amount: "150 mL",
    grams: 15,
    icon: "cup-hot",
  },
  {
    name: "Glucose tablets",
    amount: "3-4 tablets",
    grams: 15,
    icon: "capsule",
  },
];

// =============================================================================
// HYPO TREATMENT STEPS
// =============================================================================

export const HYPO_TREATMENT_STEPS = [
  "Take 1½ CPs (about 15g) of rapid-acting carbohydrate",
  "Wait 15 minutes",
  "Recheck blood glucose",
  "If still under 4.0 mmol/L, repeat treatment",
] as const;

// =============================================================================
// HYPO SEVERITY LEVELS
// =============================================================================

export const HYPO_SEVERITY = {
  /** Mild hypo - can self-treat */
  MILD: {
    range: { min: 3.0, max: 4.0 },
    description: "Mild hypoglycemia - treat with rapid-acting carbs",
    canSelfTreat: true,
  },

  /** Moderate hypo - needs prompt treatment */
  MODERATE: {
    range: { min: 2.5, max: 3.0 },
    description: "Moderate hypoglycemia - treat immediately",
    canSelfTreat: true,
  },

  /** Severe hypo - may need assistance */
  SEVERE: {
    range: { min: 0, max: 2.5 },
    description: "Severe hypoglycemia - may need assistance",
    canSelfTreat: false,
  },
} as const;
